// ClientPricesPage.js

import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Paper, Stack, TextField, Typography, Chip, Switch
} from '@mui/material';
import ClientPricesCsvDialog from '../components/ClientPricesCsvDialog';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import SearchForm from '../components/SearchForm';
import RelatedMenu from '../components/RelatedMenu';
import ClientTabs from '../components/ClientTabs';
import useDataFetch from '../hooks/useDataFetch';
import GlobalError from '../components/GlobalError';
import { Save } from '@mui/icons-material';
import axiosInstance from '../utils/axiosInstance';
import { useSnackbar } from '../contexts/SnackbarProvider';

const ClientPricesPage = ({ hasLimit }) => {
    const { id } = useParams();
    const { showSnackbar } = useSnackbar();
    const [clientName, setClientName] = useState('');
    const initialFilters = {
        keywords: '',
    };

    const {
        dataset,
        error,
        currentPage,
        totalPages,
        searchQuery,
        handleSearchChange,
        handleSearchSubmit,
        handleClearSearch,
        handlePageChange,
        refetch
    } = useDataFetch(`/client/${id}/prices`, initialFilters);
    useEffect(() => {
        const fetchClient = async () => {
            try {
                const res = await axiosInstance.get(`/client/${id}`);

                setClientName(res.data.client_name);
            } catch (err) {
                console.error(err);
            }
        };

        fetchClient();
    }, [id]);
    const [bulkOpen, setBulkOpen] = useState(false);

    const [editedPrices, setEditedPrices] = useState({});

    const handlePriceChange = (variantId, value) => {
        setEditedPrices(prev => ({
            ...prev,
            [variantId]: {
                ...prev[variantId],
                client_price: value
            }
        }));
    };
    const handleActiveChange = (variantId, value) => {
        setEditedPrices(prev => ({
            ...prev,
            [variantId]: {
                ...prev[variantId],
                is_active: value
            }
        }));
    };
    const handleSave = async () => {
        try {
            const items = Object.entries(editedPrices).map(([variant_id, value]) => {
                const payload = {
                    variant_id: Number(variant_id),
                };

                if ('client_price' in value) {
                    payload.client_price = value.client_price;
                }

                if ('is_active' in value) {
                    payload.is_active = value.is_active;
                }

                return payload;
            });

            if (items.length === 0) {
                showSnackbar('変更がありません', 'warning');
                return;
            }

            const res = await axiosInstance.patch(
                `/client/${id}/prices`,
                { items }
            );

            showSnackbar(
                res.data.message || '価格を更新しました',
                'success'
            );
            setEditedPrices({})
            refetch();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '更新に失敗しました',
                'error'
            );
        }
    };

    const handleBulkApply = async () => {
        try {

            const res = await axiosInstance.post(
                `/client/${id}/prices/bulk`
            );

            showSnackbar(
                res.data.message || '一括適用しました',
                'success'
            );

            setBulkOpen(false);

            window.location.reload();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '一括適用に失敗しました',
                'error'
            );
        }
    };
    const columns = [
        {
            label: '品番',
            accessor: 'sku',
            width: '140px',
        },
        {
            label: '商品名',
            accessor: 'product_name',
            width: '350px',
            render: (item) => (
                <Box>
                    <Typography>
                        {item.product_name}
                    </Typography>

                    <Typography
                        variant='caption'
                        color='text.secondary'
                    >
                        {item?.option_combination &&
                            Object.entries(item.option_combination).map(([type, value]) => (
                                <React.Fragment key={type}>
                                    {' '}
                                    {type}: {value}
                                </React.Fragment>
                            ))}
                    </Typography>
                </Box>
            )
        },
        {
            label: '通常価格',
            accessor: 'base_price',
            render: (item) =>
                `¥${Number(item.base_price).toLocaleString()}`,
            width: '140px',
        },
        {
            label: '個別価格',
            accessor: 'client_price',
            render: (item) => (
                <TextField
                    size="small"
                    type="text"
                    value={
                        editedPrices[item.variant_id]?.client_price
                        ?? item.client_price
                        ?? ''
                    }
                    inputProps={{
                        maxLength: 8,
                        inputMode: 'numeric',
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;

                        if (!/^\d*$/.test(raw)) return;
                        if (raw.length > 8) return;

                        handlePriceChange(item.variant_id, raw);
                    }}
                    placeholder="通常価格"
                    sx={{
                        width: '120px',
                        backgroundColor: editedPrices[item.variant_id]?.client_price != null
                            ? '#fff8e1'
                            : undefined
                    }}
                />
            ),
            width: '180px',
        },
        ...(hasLimit('ec') ? [{
            label: '販売可否',
            accessor: 'is_active',
            render: (item) => (
                <Switch
                    checked={
                        editedPrices[item.variant_id]?.is_active ??
                        item.is_active
                    }
                    onChange={(e) => {
                        handleActiveChange(item.variant_id, e.target.checked);
                    }}
                />
            ),
            width: '100px',
        }] : []),
    ];

    const searchFields = useMemo(() => {
        return [
            {
                name: 'keywords',
                label: '商品検索',
                type: 'text',
            },
        ];
    }, []);

    if (error) {
        return <GlobalError errorMessage={error} />;
    }

    return (
        <>
            <Typography variant="h1">
                <Chip label='取引先情報' sx={{ mb: 2, fontWeight: 'normal' }} /><br />
                {clientName}
            </Typography>
            <ClientTabs clientId={id} />
            <div className="grid2-2_1">
                <SearchForm
                    searchQuery={searchQuery}
                    searchFields={searchFields}
                    handleSearchChange={handleSearchChange}
                    handleSearchSubmit={handleSearchSubmit}
                    handleClearSearch={handleClearSearch}
                />

                <RelatedMenu>
                    <Button
                        variant="related-menu"
                        onClick={() => setBulkOpen(true)}
                    >
                        <Save />
                        仕切り率適用
                    </Button>
                    <ClientPricesCsvDialog clientId={id} />
                </RelatedMenu>
            </div>

            <Paper variant="section">

                <TableView
                    dataset={dataset}
                    columns={columns}
                />

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    mb={2}
                >
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                    >
                        保存
                        {Object.keys(editedPrices).length > 0 &&
                            ` (${Object.keys(editedPrices).length})`}
                    </Button>
                </Stack>
            </Paper>

            <Dialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    仕切り率一括適用
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ mt: 2 }}>
                        現在設定されている仕切り率を全商品へ適用します。
                    </Typography>

                    <Typography color="error" mt={2}>
                        既存の個別価格も上書きされます。
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setBulkOpen(false)}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkApply}
                    >
                        適用
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ClientPricesPage;